// Shop location privacy shared by the app and the Worker (SPEC section 9,
// PLAN section 4). A shop with a stall chooses to publish its exact pin. A
// home seller publishes only an area name and a point on a ~500 m grid for
// the area circle. The exact point stays in Worker-only records and reaches a
// buyer only while that buyer's paid order is accepted and not yet finished.

// 0.0045 degrees is about 500 m of latitude, and slightly less of longitude
// away from the equator. The legacy website uses the same grid
// (js/features/location-privacy.js) until it is retired.
export const PUBLIC_LOCATION_GRID_DEGREES = 0.0045;
export const AREA_NAME_MIN_LENGTH = 2;
export const AREA_NAME_MAX_LENGTH = 80;

// Legacy order statuses in which the shop has accepted the order and it is
// not finished. Exact pickup spots are shared only in these states.
export const ACCEPTED_ACTIVE_ORDER_STATUSES: ReadonlySet<string> = new Set([
  'Accepted', 'Processing', 'Ready for Pickup', 'Assigned', 'Delivering', 'At Door',
]);

export type LatLng = { lat: number; lng: number };

export type ShopLocationSettings = {
  showExactLocation: boolean;
  area: string;
  point: LatLng;
};

// What everyone may read. "unset" covers migrated sellers who have not chosen
// yet: area only, never a pin.
export type PublicShopLocation =
  | { mode: 'shop'; area: string; point: LatLng }
  | { mode: 'area'; area: string; approx: LatLng }
  | { mode: 'unset'; area: string };

export type ParseResult<T> =
  | { ok: true; value: T }
  | { ok: false; field: 'area' | 'point' | 'showExactLocation'; message: string };

function roundCoordinate(value: number): number {
  return Number(value.toFixed(6));
}

function gridCoordinate(value: number, min: number, max: number): number {
  const snapped = Math.round(value / PUBLIC_LOCATION_GRID_DEGREES) * PUBLIC_LOCATION_GRID_DEGREES;
  return Number(Math.min(max, Math.max(min, snapped)).toFixed(6));
}

export function isLatLng(value: unknown): value is LatLng {
  if (!value || typeof value !== 'object') return false;
  const { lat, lng } = value as Record<string, unknown>;
  return typeof lat === 'number' && typeof lng === 'number' &&
    Number.isFinite(lat) && Number.isFinite(lng) &&
    lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 &&
    !(lat === 0 && lng === 0);
}

export function publicGridPoint(point: LatLng): LatLng {
  return { lat: gridCoordinate(point.lat, -90, 90), lng: gridCoordinate(point.lng, -180, 180) };
}

// Area names are shown publicly, so they must not carry a phone number.
const PHONE_LIKE = /\d[\d\s().-]{5,}\d/;

export function normalizeAreaName(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

export function parseShopLocationSettings(raw: unknown): ParseResult<ShopLocationSettings> {
  const input = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  if (typeof input.showExactLocation !== 'boolean') {
    return { ok: false, field: 'showExactLocation', message: 'Choose whether to show your exact shop location.' };
  }
  const area = normalizeAreaName(input.area);
  if (area.length < AREA_NAME_MIN_LENGTH || area.length > AREA_NAME_MAX_LENGTH) {
    return { ok: false, field: 'area', message: `Enter an area name of ${AREA_NAME_MIN_LENGTH} to ${AREA_NAME_MAX_LENGTH} characters.` };
  }
  if (PHONE_LIKE.test(area)) {
    return { ok: false, field: 'area', message: 'Area names are public, so they cannot include a phone number.' };
  }
  if (!isLatLng(input.point)) {
    return { ok: false, field: 'point', message: 'Drop a pin on your shop location.' };
  }
  return {
    ok: true,
    value: {
      showExactLocation: input.showExactLocation,
      area,
      point: { lat: roundCoordinate(input.point.lat), lng: roundCoordinate(input.point.lng) },
    },
  };
}

export function publicShopLocation(settings: ShopLocationSettings): PublicShopLocation {
  return settings.showExactLocation
    ? { mode: 'shop', area: settings.area, point: { ...settings.point } }
    : { mode: 'area', area: settings.area, approx: publicGridPoint(settings.point) };
}

// Reads a public location written by the Worker, or by nothing yet.
export function readPublicShopLocation(raw: unknown, fallbackArea = ''): PublicShopLocation {
  const value = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const area = normalizeAreaName(value.area) || normalizeAreaName(fallbackArea);
  if (value.mode === 'shop' && isLatLng(value.point)) return { mode: 'shop', area, point: value.point };
  if (value.mode === 'area' && isLatLng(value.approx)) return { mode: 'area', area, approx: value.approx };
  return { mode: 'unset', area };
}

export type OrderForLocation = {
  buyerId?: unknown;
  shopId?: unknown;
  sellerId?: unknown;
  status?: unknown;
  paymentStatus?: unknown;
};

// A home seller's exact spot is released to one buyer while their paid order
// from that shop is accepted and not finished.
export function orderEntitlesExactShopLocation(order: OrderForLocation | null | undefined, viewerId: string, shopId: string): boolean {
  if (!order || !viewerId || !shopId) return false;
  const orderShopId = String(order.shopId || order.sellerId || '');
  return order.buyerId === viewerId &&
    orderShopId === shopId &&
    order.paymentStatus === 'paid' &&
    ACCEPTED_ACTIVE_ORDER_STATUSES.has(String(order.status || ''));
}

// Straight-line distance for "Near you" (great circle, mean Earth radius).
export function distanceMeters(from: LatLng, to: LatLng): number {
  const radians = (degrees: number) => (degrees * Math.PI) / 180;
  const dLat = radians(to.lat - from.lat);
  const dLng = radians(to.lng - from.lng);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(radians(from.lat)) * Math.cos(radians(to.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371008.8 * Math.asin(Math.min(1, Math.sqrt(a)));
}

// "570 m", "1.7 km", "80 km", as the design writes them.
export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.max(10, Math.round(meters / 10) * 10)} m`;
  return meters < 10000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters / 1000)} km`;
}
